import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { accountsApi, strategiesApi, TradingAccount, Strategy } from '../services/api';

const Accounts: React.FC = () => {
  const { token } = useAuth();
  const [accounts, setAccounts] = useState<TradingAccount[]>([]);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    accountType: 'paper' as 'paper' | 'live',
    apiKey: '',
    apiSecret: '',
    isDefault: false,
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [togglingStrategy, setTogglingStrategy] = useState<number | null>(null);

  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadAccounts = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const [accountsData, strategiesData] = await Promise.all([
        accountsApi.listAccounts(token),
        strategiesApi.listStrategies(token)
      ]);
      setAccounts(accountsData);
      setStrategies(strategiesData);
    } catch (err: any) {
      console.error('Failed to load accounts:', err);
      setError(err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoTrading = async (strategyId: number, currentState: boolean) => {
    if (!token) return;

    const confirmMsg = currentState
      ? 'Disable auto-trading for this strategy? Scheduled trades will not execute.'
      : 'Enable auto-trading for this strategy? Trades will execute automatically at scheduled times.';

    if (!window.confirm(confirmMsg)) return;

    setTogglingStrategy(strategyId);
    setError(null);

    try {
      const updatedStrategy = await strategiesApi.toggleAutoTrading(strategyId, token);
      setStrategies(strategies.map(s => s.id === strategyId ? updatedStrategy : s));
    } catch (err: any) {
      console.error('Failed to toggle auto-trading:', err);
      setError(err.response?.data?.detail || 'Failed to toggle auto-trading');
    } finally {
      setTogglingStrategy(null);
    }
  };

  const getStrategiesForAccount = (accountId: number) => {
    return strategies.filter(s => s.account_id === accountId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setSubmitting(true);
    setError(null);

    try {
      await accountsApi.createAccount({
        name: formData.name,
        account_type: formData.accountType,
        broker: 'alpaca',
        api_key: formData.apiKey,
        api_secret: formData.apiSecret,
        is_default: formData.isDefault,
        notes: formData.notes
      }, token);

      // Reset form and reload accounts
      setFormData({
        name: '',
        accountType: 'paper',
        apiKey: '',
        apiSecret: '',
        isDefault: false,
        notes: ''
      });
      setShowAddForm(false);
      await loadAccounts();
    } catch (err: any) {
      console.error('Failed to create account:', err);
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (accountId: number) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to delete this account?')) return;

    try {
      await accountsApi.deleteAccount(accountId, token);
      await loadAccounts();
    } catch (err: any) {
      console.error('Failed to delete account:', err);
      setError(err.response?.data?.detail || 'Failed to delete account');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <div style={{ fontSize: '18px', color: '#6b7280' }}>
            Loading accounts...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1e293b',
              margin: '0 0 8px 0'
            }}>
              Trading Accounts
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: 0
            }}>
              Manage your Alpaca trading accounts and API credentials
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              padding: '10px 20px',
              backgroundColor: showAddForm ? '#6b7280' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            {showAddForm ? 'Cancel' : '+ Add Account'}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            padding: '12px 16px',
            color: '#b91c1c'
          }}>
            {error}
          </div>
        )}

        {/* Add Account Form */}
        {showAddForm && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            padding: '24px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#1e293b',
              margin: '0 0 20px 0'
            }}>
              Add New Account
            </h3>
            <form onSubmit={handleSubmit}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                marginBottom: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Account Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="My Alpaca Account"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Account Type *
                  </label>
                  <select
                    value={formData.accountType}
                    onChange={(e) => setFormData({ ...formData, accountType: e.target.value as 'paper' | 'live' })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="paper">Paper Trading</option>
                    <option value="live">Live Trading</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Alpaca API Key *
                </label>
                <input
                  type="text"
                  required
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  placeholder="PKxxxxxxxxxxxxxxxxxx"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Alpaca API Secret *
                </label>
                <input
                  type="password"
                  required
                  value={formData.apiSecret}
                  onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })}
                  placeholder="••••••••••••••••••••"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Notes (optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about this account..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{
                    fontSize: '14px',
                    color: '#374151'
                  }}>
                    Set as default account
                  </span>
                </label>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                paddingTop: '16px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: submitting ? '#9ca3af' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submitting ? 'Creating...' : 'Create Account'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Accounts List */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#1e293b',
              margin: 0
            }}>
              Your Accounts
            </h3>
          </div>

          {accounts.length === 0 ? (
            <div style={{
              padding: '60px 24px',
              textAlign: 'center',
              color: '#9ca3af'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>💼</div>
              <p style={{ fontSize: '16px', marginBottom: '8px' }}>No trading accounts yet</p>
              <p style={{ fontSize: '14px' }}>Click "Add Account" to connect your Alpaca account</p>
            </div>
          ) : (
            <div>
              {accounts.map((account) => {
                const accountStrategies = getStrategiesForAccount(account.id);
                return (
                  <div
                    key={account.id}
                    style={{
                      padding: '20px 24px',
                      borderBottom: '1px solid #f3f4f6'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '8px'
                        }}>
                          <h4 style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#1e293b',
                            margin: 0
                          }}>
                            {account.name}
                          </h4>
                          {account.is_default && (
                            <span style={{
                              padding: '2px 8px',
                              backgroundColor: '#dbeafe',
                              color: '#1e40af',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              Default
                            </span>
                          )}
                          <span style={{
                            padding: '2px 8px',
                            backgroundColor: account.account_type === 'paper' ? '#fef3c7' : '#dcfce7',
                            color: account.account_type === 'paper' ? '#92400e' : '#166534',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            textTransform: 'capitalize'
                          }}>
                            {account.account_type}
                          </span>
                          <span style={{
                            padding: '2px 8px',
                            backgroundColor: account.is_active ? '#dcfce7' : '#fee2e2',
                            color: account.is_active ? '#166534' : '#991b1b',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {account.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: '#6b7280'
                        }}>
                          <div>Broker: {account.broker}</div>
                          {account.account_number && <div>Account: {account.account_number}</div>}
                          <div>Created: {new Date(account.created_at).toLocaleDateString()}</div>
                          {account.last_sync && (
                            <div>Last sync: {new Date(account.last_sync).toLocaleString()}</div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(account.id)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>

                    {/* Strategies Section */}
                    {accountStrategies.length > 0 && (
                      <div style={{
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <h5 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          margin: '0 0 12px 0'
                        }}>
                          Strategies
                        </h5>
                        {accountStrategies.map((strategy) => (
                          <div
                            key={strategy.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '12px 16px',
                              backgroundColor: '#f9fafb',
                              borderRadius: '6px',
                              marginBottom: '8px'
                            }}
                          >
                            <div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '4px'
                              }}>
                                <span style={{
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: '#1e293b'
                                }}>
                                  {strategy.name}
                                </span>
                                <span style={{
                                  padding: '2px 6px',
                                  backgroundColor: '#e0e7ff',
                                  color: '#3730a3',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500'
                                }}>
                                  {strategy.strategy_type}
                                </span>
                              </div>
                              <div style={{
                                fontSize: '12px',
                                color: '#6b7280'
                              }}>
                                {strategy.total_trades} trades | P&L: ${strategy.total_pnl.toFixed(2)}
                                {strategy.last_executed && (
                                  <span> | Last run: {new Date(strategy.last_executed).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <span style={{
                                fontSize: '13px',
                                fontWeight: '500',
                                color: strategy.is_automated ? '#166534' : '#6b7280'
                              }}>
                                Auto-trade: {strategy.is_automated ? 'On' : 'Off'}
                              </span>
                              <button
                                onClick={() => handleToggleAutoTrading(strategy.id, strategy.is_automated)}
                                disabled={togglingStrategy === strategy.id}
                                style={{
                                  position: 'relative',
                                  width: '44px',
                                  height: '24px',
                                  borderRadius: '12px',
                                  border: 'none',
                                  backgroundColor: strategy.is_automated ? '#10b981' : '#d1d5db',
                                  cursor: togglingStrategy === strategy.id ? 'wait' : 'pointer',
                                  transition: 'background-color 0.2s',
                                  opacity: togglingStrategy === strategy.id ? 0.6 : 1
                                }}
                                title={strategy.is_automated ? 'Click to disable auto-trading' : 'Click to enable auto-trading'}
                              >
                                <span style={{
                                  position: 'absolute',
                                  top: '2px',
                                  left: strategy.is_automated ? '22px' : '2px',
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  backgroundColor: 'white',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                  transition: 'left 0.2s'
                                }} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {accountStrategies.length === 0 && (
                      <div style={{
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid #e5e7eb',
                        fontSize: '13px',
                        color: '#9ca3af',
                        fontStyle: 'italic'
                      }}>
                        No strategies configured for this account
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div style={{
          backgroundColor: '#eff6ff',
          border: '1px solid #3b82f6',
          borderRadius: '8px',
          padding: '20px 24px'
        }}>
          <h4 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#1e40af',
            margin: '0 0 12px 0'
          }}>
            How to get your Alpaca API keys
          </h4>
          <ol style={{
            margin: 0,
            paddingLeft: '20px',
            fontSize: '14px',
            color: '#1e40af',
            lineHeight: '1.6'
          }}>
            <li>Go to <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer" style={{ color: '#1e40af', textDecoration: 'underline' }}>alpaca.markets</a> and sign up for an account</li>
            <li>Navigate to your account dashboard</li>
            <li>Click on "Paper Trading" or "Live Trading" in the sidebar</li>
            <li>Go to "API Keys" section</li>
            <li>Generate a new API key and secret</li>
            <li>Copy both the API Key and API Secret and paste them above</li>
          </ol>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '13px',
            color: '#1e40af'
          }}>
            <strong>Note:</strong> For testing, start with a Paper Trading account. Your API keys are stored securely and encrypted in the database.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Accounts;
